import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, HelpCircle, FileText, MessageSquarePlus, History,
  Bug, Lightbulb, ImagePlus, Send, CheckCircle2, Clock, AlertCircle, X,
} from "lucide-react";
import {
  API_BASE_URL,
  getRoleFromToken,
  postCentralAjudaFeedback,
  getMeusFeedbacksCentralAjuda,
} from "../../api";
import { MODULOS_DOCUMENTACAO } from "./content";
import { getIconeModulo } from "./icones";

const STATUS_LABEL = {
  aberto: { label: "Aberto", cor: "bg-amber-100 text-amber-700", icone: Clock },
  em_analise: { label: "Em análise", cor: "bg-blue-100 text-blue-700", icone: AlertCircle },
  resolvido: { label: "Resolvido", cor: "bg-emerald-100 text-emerald-700", icone: CheckCircle2 },
};

const TABS = [
  { id: "documentacao", label: "Documentação", icon: FileText },
  { id: "feedback", label: "Sugestões e Bugs", icon: MessageSquarePlus },
  { id: "historico", label: "Meus Envios", icon: History },
  
];

function FormularioFeedback({ modulosVisiveis, onEnviado }) {
  const [tipo, setTipo] = useState("sugestao");
  const [moduloId, setModuloId] = useState("");
  const [titulo, setTitulo] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [imagem, setImagem] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // { tipo: 'success' | 'error', texto }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!titulo.trim() || !mensagem.trim()) {
      setResultado({ tipo: "error", texto: "Preencha o título e a mensagem." });
      return;
    }
    setEnviando(true);
    setResultado(null);
    try {
      const formData = new FormData();
      formData.append("tipo", tipo);
      formData.append("titulo", titulo.trim());
      formData.append("mensagem", mensagem.trim());
      if (moduloId) formData.append("modulo_id", moduloId);
      if (imagem) formData.append("imagem", imagem);

      await postCentralAjudaFeedback(formData);
      setResultado({ tipo: "success", texto: "Envio registrado com sucesso. Obrigado pelo retorno!" });
      setTitulo("");
      setMensagem("");
      setImagem(null);
      onEnviado?.();
    } catch (err) {
      setResultado({ tipo: "error", texto: err.message || "Não foi possível enviar. Tente novamente." });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 max-w-2xl">
      <div className="flex gap-3 mb-5">
        <button
          type="button"
          onClick={() => setTipo("sugestao")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-semibold transition-all ${
            tipo === "sugestao"
              ? "bg-purple-50 border-purple-300 text-purple-700"
              : "border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}
        >
          <Lightbulb className="w-4 h-4" /> Sugestão de melhoria
        </button>
        <button
          type="button"
          onClick={() => setTipo("bug")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-semibold transition-all ${
            tipo === "bug"
              ? "bg-red-50 border-red-300 text-red-700"
              : "border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}
        >
          <Bug className="w-4 h-4" /> Reportar bug
        </button>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Módulo relacionado (opcional)</label>
        <select
          value={moduloId}
          onChange={(e) => setModuloId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-purple-400"
        >
          <option value="">Geral / outro</option>
          {modulosVisiveis.map((m) => (
            <option key={m.id} value={m.id}>{m.titulo}</option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Título</label>
        <input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder={tipo === "bug" ? "Ex: Erro ao salvar filtro na tela X" : "Ex: Adicionar filtro por trimestre"}
          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-purple-400"
        />
      </div>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Mensagem</label>
        <textarea
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
          rows={5}
          placeholder="Descreva com detalhes. Se for um bug, inclua os passos para reproduzir."
          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-purple-400 resize-none"
        />
      </div>

      {tipo === "bug" && (
        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Anexar imagem (opcional)</label>
          <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 w-fit">
            <ImagePlus className="w-4 h-4" />
            {imagem ? imagem.name : "Escolher imagem"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setImagem(e.target.files?.[0] || null)} />
          </label>
          {imagem && (
            <button
              type="button"
              onClick={() => setImagem(null)}
              className="mt-1.5 flex items-center gap-1 text-xs text-gray-400 hover:text-red-500"
            >
              <X className="w-3 h-3" /> remover anexo
            </button>
          )}
        </div>
      )}

      {resultado && (
        <div
          className={`mb-4 text-sm rounded-lg px-3 py-2.5 ${
            resultado.tipo === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
          }`}
        >
          {resultado.texto}
        </div>
      )}

      <button
        type="submit"
        disabled={enviando}
        className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-purple-900 to-pink-800 text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <Send className="w-4 h-4" /> {enviando ? "Enviando..." : "Enviar"}
      </button>
    </form>
  );
}

function Historico({ envios, carregando, erro }) {
  if (carregando) {
    return <div className="text-center py-16 text-gray-400 text-sm">Carregando seus envios...</div>;
  }
  if (erro) {
    return <div className="text-center py-16 text-red-500 text-sm">{erro}</div>;
  }
  if (envios.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">
        Você ainda não enviou nenhuma sugestão ou reporte de bug.
      </div>
    );
  }
  return (
    <div className="space-y-3 max-w-3xl">
      {envios.map((f) => {
        const status = STATUS_LABEL[f.status] || STATUS_LABEL.aberto;
        const StatusIcon = status.icone;
        return (
          <div key={f.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                {f.tipo === "bug" ? (
                  <Bug className="w-4 h-4 text-red-500" />
                ) : (
                  <Lightbulb className="w-4 h-4 text-purple-500" />
                )}
                <span className="font-semibold text-gray-800 text-sm">{f.titulo}</span>
              </div>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${status.cor}`}>
                <StatusIcon className="w-3 h-3" /> {status.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-2 whitespace-pre-wrap">{f.mensagem}</p>
            {f.imagem_path && (
              <a href={`${API_BASE_URL}${f.imagem_path}`} target="_blank" rel="noreferrer" className="inline-block mt-3">
                <img src={`${API_BASE_URL}${f.imagem_path}`} alt="Anexo do reporte" className="max-h-40 rounded-lg border border-gray-200" />
              </a>
            )}
            {f.resposta_admin && (
              <div className="mt-3 bg-gray-50 border border-gray-100 rounded-lg p-3 text-xs text-gray-600">
                <span className="font-semibold text-gray-700">Retorno da equipe: </span>{f.resposta_admin}
              </div>
            )}
            <div className="text-xs text-gray-400 mt-3">
              {new Date(f.criado_em).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CentralAjuda() {
  const navigate = useNavigate();
  const userRole = getRoleFromToken();
  const [aba, setAba] = useState("documentacao");
  const [envios, setEnvios] = useState([]);
  const [carregandoEnvios, setCarregandoEnvios] = useState(true);
  const [erroEnvios, setErroEnvios] = useState(null);

  const modulosVisiveis = MODULOS_DOCUMENTACAO.filter(
    (m) => !m.allowedRoles || m.allowedRoles.includes(userRole),
  );

  // Para o seletor de feedback, os sub-módulos agrupados (ex.: Margem Bruta,
  // dentro de "Módulos Operacionais") aparecem soltos, para permitir marcar
  // o feedback no módulo específico em vez de só no grupo.
  const modulosFeedbackVisiveis = MODULOS_DOCUMENTACAO
    .flatMap((m) => m.subModulos || [m])
    .filter((m) => !m.allowedRoles || m.allowedRoles.includes(userRole));

  async function carregarEnvios() {
    setCarregandoEnvios(true);
    setErroEnvios(null);
    try {
      const dados = await getMeusFeedbacksCentralAjuda();
      setEnvios(dados || []);
    } catch (e) {
      setErroEnvios(e.message || "Não foi possível carregar seus envios.");
    } finally {
      setCarregandoEnvios(false);
    }
  }

  useEffect(() => {
    if (aba === "historico") carregarEnvios();
  }, [aba]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/inicio")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <HelpCircle className="w-5 h-5" />
          <span className="text-lg font-bold tracking-tight">Central de Ajuda</span>
        </div>
        <button
          onClick={() => navigate("/inicio")}
          className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
        >
          Menu Principal
        </button>
      </header>

      <div className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 pb-8 pt-2">
        <div className="max-w-5xl mx-auto">
          <p className="text-purple-200 text-sm max-w-xl">
            Documentação dos módulos que você tem acesso, além de um canal para enviar sugestões de melhoria e reportar bugs.
          </p>
        </div>
      </div>

      <main className="flex-1 px-6 py-8 -mt-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex gap-2 mb-8 bg-white rounded-xl border border-gray-100 shadow-sm p-1.5 w-fit">
            {TABS.map((t) => {
              const Icon = t.icon;
              const ativo = aba === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setAba(t.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    ativo ? "bg-gradient-to-r from-purple-900 to-pink-800 text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>

          {aba === "documentacao" && (
            modulosVisiveis.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">
                Nenhuma documentação disponível para o seu perfil de acesso no momento.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {modulosVisiveis.map((m) => {
                  const Icon = getIconeModulo(m.icone);
                  return (
                    <button
                      key={m.id}
                      onClick={() => navigate(`/central-ajuda/${m.id}`)}
                      className="group text-left bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col gap-4"
                    >
                      <div className="bg-gradient-to-br from-purple-900 to-pink-800 text-white p-3 rounded-xl shadow-md group-hover:scale-105 transition-transform duration-200 w-fit">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-gray-800 mb-1 group-hover:text-pink-700 transition-colors">
                          {m.titulo}
                        </h2>
                        <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">{m.descricao}</p>
                      </div>
                      <span className="text-xs font-medium text-pink-700 group-hover:underline mt-auto">
                        Ver documentação →
                      </span>
                    </button>
                  );
                })}
              </div>
            )
          )}

          {aba === "feedback" && (
            <FormularioFeedback modulosVisiveis={modulosFeedbackVisiveis} onEnviado={carregarEnvios} />
          )}

          {aba === "historico" && (
            <Historico envios={envios} carregando={carregandoEnvios} erro={erroEnvios} />
          )}
        </div>
      </main>
    </div>
  );
}
